import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { Sidebar } from "./sidebar";
import { Header } from "./header";
import { ScrollArea } from "@/components/ui/scroll-area";

const SIDEBAR_COLLAPSED_KEY = "compligraph:sidebar:collapsed";

/**
 * Root application shell layout.
 *
 * Renders the sidebar, header, and main content area. The sidebar's
 * collapsed state is persisted to localStorage across sessions.
 * The main content area uses ScrollArea for overflow handling.
 *
 * Layout structure:
 *   ┌────────────────────────────────────────────┐
 *   │ Header (full width)                        │
 *   ├──────────┬─────────────────────────────────┤
 *   │ Sidebar  │  <Outlet /> (page content)      │
 *   └──────────┴─────────────────────────────────┘
 */
export function AppLayout() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      // localStorage may be unavailable in some environments
    }
  }, [collapsed]);

  const handleToggle = () => setCollapsed((prev) => !prev);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar collapsed={collapsed} onToggle={handleToggle} />
        <main className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-6">
              <Outlet />
            </div>
          </ScrollArea>
        </main>
      </div>
    </div>
  );
}
